// Language context for i18n support - v2
import { createContext, useContext, useState, useEffect, ReactNode, type Context } from 'react';
import { Language, detectBrowserLanguage, getLanguageInfo, LanguageInfo } from './types';
import { translations, Translations } from './translations';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  languageInfo: LanguageInfo;
}

// NOTE: During dev hot-reloads, modules in the i18n graph (translations, etc.) can refresh.
// If the Context object is recreated, existing mounted trees can temporarily end up with
// a Provider/Consumer mismatch, resulting in: "useLanguage must be used within a LanguageProvider".
// Caching the context on globalThis stabilizes identity across HMR.
const globalForI18n = globalThis as unknown as {
  __veectoriaLanguageContext?: Context<LanguageContextType | undefined>;
};

const LanguageContext =
  globalForI18n.__veectoriaLanguageContext ??
  createContext<LanguageContextType | undefined>(undefined);

globalForI18n.__veectoriaLanguageContext = LanguageContext;

const STORAGE_KEY = 'veectoria-language';

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      // Check localStorage first
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && translations[stored as Language]) {
        return stored as Language;
      }
    } catch {
      // Some browsers / privacy modes can block access to localStorage.
      // In that case we fall back to browser language detection.
    }

    // Otherwise detect from browser
    return detectBrowserLanguage();
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // Ignore storage write errors.
    }
  };

  // Update document direction for RTL languages
  useEffect(() => {
    const info = getLanguageInfo(language);
    document.documentElement.dir = info.dir;
    document.documentElement.lang = language;
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations[language],
    languageInfo: getLanguageInfo(language),
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
